import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon } from 'lucide-react';
const HALLWAY_IMG = 'https://cdn.magicpatterns.com/uploads/9wBDqLBD3G7mGJAQzHQLSN/20260601_162425[1].jpg';
export type HallwayRoomId = 'sculpting' | 'biocharging' | 'laser';
interface DoorHotspot {
  id: HallwayRoomId;
  label: string;
  // position over the real door, % of scene
  x: number;
  y: number;
  w: number;
  h: number;
}
// Door order (from the client): 2nd door on left = Sculpting,
// 3rd door on left = Biocharging, door straight ahead = Laser Hair Removal
const doors: DoorHotspot[] = [{
  id: 'sculpting',
  label: 'Body Sculpting',
  x: 28,
  y: 50,
  w: 11,
  h: 30
}, {
  id: 'biocharging',
  label: 'Biocharging',
  x: 40,
  y: 47,
  w: 8,
  h: 24
}, {
  id: 'laser',
  label: 'Laser Hair Removal',
  x: 58,
  y: 50,
  w: 11,
  h: 30
}];
interface Props {
  open: boolean;
  onClose: () => void;
  onPickRoom: (id: HallwayRoomId) => void;
}
export function HallwayScene({
  open,
  onClose,
  onPickRoom
}: Props) {
  const sparkles = useMemo(() => Array.from({
    length: 12
  }).map((_, i) => ({
    id: i,
    left: 15 + Math.random() * 70,
    top: 20 + Math.random() * 55,
    size: 5 + Math.random() * 9,
    duration: 2.5 + Math.random() * 3,
    delay: Math.random() * 4
  })), []);
  return <AnimatePresence data-id="element-54">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} className="fixed inset-0 z-[160] bg-aubergine-900 overflow-hidden" data-id="element-55">
          {/* Real hallway photo */}
          <motion.div initial={{
        scale: 1.06
      }} animate={{
        scale: 1
      }} transition={{
        duration: 1.4,
        ease: [0.65, 0, 0.35, 1]
      }} className="absolute inset-0" data-id="element-56">
            <img src={HALLWAY_IMG} alt="Wonder of Life Wellness Spa treatment hallway with green carpet and white doors" className="w-full h-full object-cover" style={{
          objectPosition: 'center 35%'
        }} data-id="element-57" />
            <div className="absolute inset-0 bg-gradient-to-b from-aubergine-900/30 via-transparent to-aubergine-900/45" data-id="element-58" />
          </motion.div>

          {/* Sparkle glam */}
          {sparkles.map(s => <motion.div key={s.id} animate={{
        opacity: [0, 0.8, 0],
        scale: [0.4, 1, 0.4]
      }} transition={{
        duration: s.duration,
        repeat: Infinity,
        delay: s.delay,
        ease: 'easeInOut'
      }} className="absolute pointer-events-none" style={{
        left: `${s.left}%`,
        top: `${s.top}%`
      }} data-id="element-59">
              <SparklesIcon size={s.size} className="text-gold-300" style={{
          filter: 'drop-shadow(0 0 5px rgba(224,196,126,0.8))'
        }} data-id="element-60" />
            </motion.div>)}

          {/* Header */}
          <div className="absolute top-5 left-1/2 -translate-x-1/2 z-30 text-center px-6" data-id="element-61">
            <div className="flex items-center justify-center gap-2 mb-2" data-id="element-62">
              <SparklesIcon size={13} className="text-gold-300" data-id="element-63" />
              <span className="text-[10px] uppercase tracking-luxe text-gold-300 drop-shadow-[0_1px_8px_rgba(28,13,38,0.9)]" data-id="element-64">
                Down the Hall
              </span>
              <SparklesIcon size={13} className="text-gold-300" data-id="element-65" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-cream-50 drop-shadow-[0_2px_16px_rgba(28,13,38,0.9)]" data-id="element-66">
              Choose a room
            </h2>
          </div>

          {/* Close */}
          <button onClick={onClose} aria-label="Back" className="absolute top-5 right-5 z-40 w-11 h-11 rounded-full bg-aubergine-800/80 backdrop-blur border border-gold-400/30 flex items-center justify-center text-cream-50 hover:bg-aubergine-700 transition-colors" data-id="element-67">
            <XIcon size={18} data-id="element-68" />
          </button>

          {/* Door hotspots */}
          {doors.map(d => <button key={d.id} onClick={() => onPickRoom(d.id)} className="absolute group focus:outline-none cursor-pointer z-20" style={{
        left: `${d.x}%`,
        top: `${d.y}%`,
        width: `${d.w}%`,
        height: `${d.h}%`,
        transform: 'translate(-50%, -50%)'
      }} aria-label={`Enter ${d.label}`} data-id="element-69">
              {/* Glow over the door */}
              <motion.div animate={{
          opacity: [0.25, 0.5, 0.25]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-lg pointer-events-none group-hover:opacity-90 transition-opacity" style={{
          background: 'radial-gradient(ellipse at center, rgba(224,196,126,0.45) 0%, transparent 70%)',
          filter: 'blur(10px)'
        }} data-id="element-70" />
              {/* Outline ring */}
              <div className="absolute inset-0 rounded-lg border border-gold-300/50 group-hover:border-gold-300 transition-colors pointer-events-none" style={{
          boxShadow: '0 0 18px rgba(224,196,126,0.25)'
        }} data-id="element-71" />
              {/* Label chip */}
              <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 rounded-full bg-aubergine-800/90 backdrop-blur border border-gold-400/30 text-[9px] uppercase tracking-[0.25em] text-cream-50 group-hover:bg-aubergine-700 transition-colors" data-id="element-72">
                {d.label}
              </span>
            </button>)}
        </motion.div>}
    </AnimatePresence>;
}